import { useState, useEffect, useRef } from "react";
import {
  Grid,
  Stack,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Paper,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
  Alert,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import LockIcon from "@mui/icons-material/Lock";
import { toMarkdownWithOptions } from "../../client/ToMarkdown";
import { fetchJieQiDates } from "../../utils/baziCalculator";
import { getGanZhiColor, getIsYang } from "../StyleControl";

function isMember(user) {
  // 所有功能免费开放，不再校验会员状态（原判断逻辑保留注释，可随时恢复）：
  // if (!user) return false;
  // if (user.is_member !== undefined) return user.is_member;
  // if (user.member_type === 0) return false;
  // if (user.member_expires_at) {
  //   return new Date(user.member_expires_at) > new Date();
  // }
  // return true;
  return true;
}

export default function AiZhiLingPage({ data, state, setState, user, onMemberClick }) {
  const textFieldRef = useRef(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const {
    selectedDaYun,
    selectedLiuNian,
    includeLiuYue,
    includeLiuRi,
    includeLiuShi,
    previewText,
  } = state;

  const setSelectedDaYun = (v) =>
    setState((prev) => ({ ...prev, selectedDaYun: v }));
  const setSelectedLiuNian = (v) =>
    setState((prev) => ({ ...prev, selectedLiuNian: v }));
  const setIncludeLiuYue = (v) =>
    setState((prev) => ({ ...prev, includeLiuYue: v }));
  const setIncludeLiuRi = (v) =>
    setState((prev) => ({ ...prev, includeLiuRi: v }));
  const setIncludeLiuShi = (v) =>
    setState((prev) => ({ ...prev, includeLiuShi: v }));
  const setPreviewText = (v) =>
    setState((prev) => ({ ...prev, previewText: v }));

  // 仅在首次挂载且 previewText 为空时生成默认四柱内容
  const hasGeneratedRef = useRef(false);
  useEffect(() => {
    if (data && !previewText && !hasGeneratedRef.current) {
      hasGeneratedRef.current = true;
      generateMarkdown({
        daYunYears: selectedDaYun,
        liuNianYears: selectedLiuNian,
        includeLiuYue,
        includeLiuRi,
        includeLiuShi,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleDaYunChange = (event, newValue) => {
    const removed = selectedDaYun.filter((y) => !newValue.includes(y));
    let newLiuNian = [...selectedLiuNian];
    removed.forEach((year) => {
      newLiuNian = newLiuNian.filter((y) => y < year || y >= year + 10);
    });
    setSelectedDaYun(newValue);
    setSelectedLiuNian(newLiuNian);
  };

  const handleLiuNianChange = (event, newValue) => {
    const added = newValue.filter((y) => !selectedLiuNian.includes(y));
    let newDaYun = [...selectedDaYun];
    added.forEach((year) => {
      const daYunYear = daYunYears.find((dy) => year >= dy && year < dy + 10);
      if (daYunYear && !newDaYun.includes(daYunYear)) {
        newDaYun.push(daYunYear);
      }
    });
    newDaYun.sort((a, b) => a - b);
    setSelectedLiuNian(newValue);
    setSelectedDaYun(newDaYun);
  };

  const generateMarkdown = async (opts) => {
    if (!data) return;
    let jieQiCache = {};
    const needsJieQi = opts.includeLiuRi || opts.includeLiuShi;
    if (needsJieQi && opts.liuNianYears.length > 0) {
      const results = await Promise.all(
        opts.liuNianYears.map((year) =>
          fetchJieQiDates(year).then((d) => ({ year, data: d }))
        )
      );
      results.forEach(({ year, data: d }) => {
        jieQiCache[year] = d;
      });
    }
    const md = toMarkdownWithOptions(data, {
      daYunYears: opts.daYunYears,
      liuNianYears: opts.liuNianYears,
      includeLiuYue: opts.includeLiuYue,
      includeLiuRi: opts.includeLiuRi,
      includeLiuShi: opts.includeLiuShi,
      jieQiCache,
    });
    setPreviewText(md);
  };

  const needsMember = includeLiuYue || includeLiuRi || includeLiuShi;

  const handleGeneratePreview = async () => {
    if (needsMember && !isMember(user)) {
      onMemberClick?.();
      return;
    }
    await generateMarkdown({
      daYunYears: selectedDaYun,
      liuNianYears: selectedLiuNian,
      includeLiuYue,
      includeLiuRi,
      includeLiuShi,
    });
  };

  const handleMemberCheckboxChange = (setter, value) => {
    if (value && !isMember(user)) {
      onMemberClick?.();
      return;
    }
    setter(value);
  };

  const handleCopy = () => {
    if (needsMember && !isMember(user)) {
      onMemberClick?.();
      return;
    }
    const input = textFieldRef.current;
    if (!input) return;
    input.focus();
    input.select();
    input.setSelectionRange(0, input.value.length);
    try {
      document.execCommand("copy");
      setOpenSnackbar(true);
    } catch (err) {
      console.error(err);
      alert("复制失败，请手动复制！");
    }
  };

  const handleDownload = () => {
    if (needsMember && !isMember(user)) {
      onMemberClick?.();
      return;
    }
    const blob = new Blob([previewText], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const fileName = data && data.name ? `命盘分析_${data.name}.md` : "命盘分析.md";
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const daYunYears = data && data.da_yun
    ? Object.keys(data.da_yun)
        .map((y) => Number(y))
        .sort((a, b) => a - b)
    : [];

  const liuNianYears = data && data.liu_nian
    ? Object.keys(data.liu_nian)
        .map((y) => Number(y))
        .sort((a, b) => a - b)
    : [];

  if (!data) {
    return (
      <Grid size={{ xs: 12, md: 6 }} m={2}>
        <Typography>请先进行排盘</Typography>
      </Grid>
    );
  }

  return (
    <Grid size={{ xs: 12, md: 6 }} m={2}>
      <Stack direction="column" spacing={3}>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "transparent" }}>
          <Stack direction="column" spacing={2}>
            {/* 四柱信息 */}
            <Box>
              <FormControlLabel
                control={<Checkbox checked disabled />}
                label="四柱信息（必选项）"
              />
              <Paper variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: "transparent" }}>
                <Stack direction="row" spacing={2} justifyContent="space-around">
                  {[
                    { label: "年柱", zhu: data.nian_zhu },
                    { label: "月柱", zhu: data.yue_zhu },
                    { label: "日柱", zhu: data.ri_zhu },
                    { label: "时柱", zhu: data.shi_zhu },
                  ].map(({ label, zhu }) => (
                    <Stack key={label} direction="column" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {label}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{
                          fontStyle: getIsYang(zhu.gan) ? null : "italic",
                          color: getGanZhiColor(zhu.gan),
                        }}
                      >
                        {zhu.gan}
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        sx={{
                          fontStyle: getIsYang(zhu.zhi) ? null : "italic",
                          color: getGanZhiColor(zhu.zhi),
                        }}
                      >
                        {zhu.zhi}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Box>

            {/* 大运选择 */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">大运选择</Typography>
                <Button size="small" onClick={() => setSelectedDaYun(daYunYears)}>
                  全选
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedDaYun([]);
                    setSelectedLiuNian([]);
                  }}
                >
                  清空
                </Button>
              </Stack>
              <ToggleButtonGroup
                value={selectedDaYun}
                onChange={handleDaYunChange}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                {daYunYears.map((year) => {
                  const zhu = data.da_yun[year];
                  return (
                    <ToggleButton
                      key={year}
                      value={year}
                      sx={(theme) => ({
                        px: 1.5,
                        py: 0.5,
                        flexShrink: 0,
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      })}
                    >
                      <Stack direction="column" alignItems="center" spacing={0}>
                        <Typography variant="body2" fontWeight="bold">
                          {zhu?.gan || ""}{zhu?.zhi || ""}
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: 10, opacity: 0.8 }}>
                          {year}
                        </Typography>
                      </Stack>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            </Box>

            {/* 流年选择 */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">流年选择</Typography>
                <Button size="small" onClick={() => setSelectedLiuNian(liuNianYears)}>
                  全选
                </Button>
                <Button size="small" onClick={() => setSelectedLiuNian([])}>
                  清空
                </Button>
              </Stack>
              <Paper variant="outlined" sx={{ p: 1, maxHeight: 160, overflowY: "auto", bgcolor: "transparent" }}>
                <ToggleButtonGroup
                  value={selectedLiuNian}
                  onChange={handleLiuNianChange}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {liuNianYears.map((year) => (
                    <ToggleButton
                      key={year}
                      value={year}
                      sx={(theme) => ({
                        px: 1.5,
                        py: 0.5,
                        flexShrink: 0,
                        "&.Mui-selected": {
                          backgroundColor: theme.palette.primary.main,
                          color: theme.palette.primary.contrastText,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.dark,
                          },
                        },
                      })}
                    >
                      {year}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Paper>
            </Box>

            {/* 流月 / 流日 / 流时 */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                扩展内容（勾选后导出所有选中流年的对应数据）
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeLiuYue}
                      onChange={(e) => handleMemberCheckboxChange(setIncludeLiuYue, e.target.checked)}
                      icon={!isMember(user) ? <LockIcon fontSize="small" /> : undefined}
                    />
                  }
                  label="流月"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeLiuRi}
                      onChange={(e) => handleMemberCheckboxChange(setIncludeLiuRi, e.target.checked)}
                      icon={!isMember(user) ? <LockIcon fontSize="small" /> : undefined}
                    />
                  }
                  label="流日"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeLiuShi}
                      onChange={(e) => handleMemberCheckboxChange(setIncludeLiuShi, e.target.checked)}
                      icon={!isMember(user) ? <LockIcon fontSize="small" /> : undefined}
                    />
                  }
                  label="流时"
                />
              </Stack>
              {!isMember(user) && (
                <Alert
                  severity="info"
                  sx={{ mt: 1, cursor: "pointer" }}
                  onClick={onMemberClick}
                >
                  流月/流日/流时导出为会员专属功能，点击开通会员
                </Alert>
              )}
            </Box>

            <Stack direction="row" spacing={2} alignSelf="flex-start">
              <Button
                variant="contained"
                startIcon={<CachedIcon />}
                onClick={handleGeneratePreview}
              >
                生成
              </Button>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopy}
                disabled={!previewText}
              >
                复制
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={!previewText}
              >
                下载
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* 预览区 */}
        {previewText && (
          <Box sx={{ pt: 1 }}>
            <Stack direction="column" spacing={1}>
              <Typography variant="subtitle2">AI指令预览</Typography>
              <TextField
                inputRef={textFieldRef}
                multiline
                minRows={10}
                maxRows={20}
                fullWidth
                value={previewText}
                InputProps={{ readOnly: true }}
              />
            </Stack>
          </Box>
        )}
      </Stack>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          已复制到剪贴板
        </Alert>
      </Snackbar>
    </Grid>
  );
}
